/**
 * Vision's instantiation of the translation-key kit in `@thalamos/common` —
 * see `translationKit.ts` there for the mechanism and why keys collapse to a
 * branded string. This file owns only what is Vision's: the key union derived
 * from its locale bundle, its brand, and the `tk()` collapse. Everything else
 * is re-exported so callers keep one import path.
 */
import { z } from "zod";

import { buildTranslationInput as buildTranslationInputGeneric } from "@thalamos/common";
import type {
  TranslationInput as KitTranslationInput,
  TranslationKeyAndOptions as KitTranslationKeyAndOptions,
  StrictNestedKeys,
  TranslationInputBaseContext,
} from "@thalamos/common";

import common from "../locales/en/common.js";

/**
 * A strongly-typed union of all valid translation keys stored in the
 * base locale.
 */
export type TranslationKeyUnbranded = StrictNestedKeys<typeof common>;

// Exists solely to mint the branded type below.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TranslationKeySchema = z.string().brand<"vision-common:TranslationKey">();

/**
 * A simple branded type to represent a valid translation key.
 */
export type TranslationKey = z.infer<typeof TranslationKeySchema>;

/**
 * Convert a strongly-typed translation key into a less demanding branded type.
 * The collapse is what keeps the compiler alive on a key union with thousands
 * of members; the full rationale lives on the translation kit's module
 * docblock in `@thalamos/common`.
 */
export const tk = (key: TranslationKeyUnbranded): TranslationKey => {
  return key as TranslationKey;
};

export type {
  TFunction,
  TranslationInputBaseContext,
  TranslationOptions,
} from "@thalamos/common";
export { translate } from "@thalamos/common";

/**
 * A combination of a translation key and its associated options.
 */
export type TranslationKeyAndOptions =
  KitTranslationKeyAndOptions<TranslationKey>;

/**
 * Represents a TranslationKeyAndOptions, either directly or via a function.
 * The function will be called with the provided context at runtime.
 */
export type TranslationInput<Context extends TranslationInputBaseContext> =
  KitTranslationInput<TranslationKey, Context>;

/**
 * `buildTranslationInput`, pinned to Vision's branded key. The generic kit
 * function infers the key type from its argument, so a call that provides no
 * key evidence — an argument statically narrowed to `undefined` — cannot
 * recover the brand. These signatures name it, and the body delegates.
 */
export function buildTranslationInput(
  translationInput: TranslationKey | TranslationKeyAndOptions,
): TranslationKeyAndOptions;
export function buildTranslationInput(
  translationInput: TranslationKey | TranslationKeyAndOptions | undefined,
): TranslationKeyAndOptions | undefined;
export function buildTranslationInput<
  Context extends TranslationInputBaseContext,
>(
  translationInput: TranslationInput<Context>,
  context?: Context,
): TranslationKeyAndOptions;
export function buildTranslationInput<
  Context extends TranslationInputBaseContext,
>(
  translationInput: TranslationInput<Context> | undefined,
  context?: Context,
): TranslationKeyAndOptions | undefined;
export function buildTranslationInput<
  Context extends TranslationInputBaseContext,
>(
  translationInput: TranslationInput<Context> | undefined,
  context?: Context,
): TranslationKeyAndOptions | undefined {
  return buildTranslationInputGeneric(translationInput, context);
}
